import React, { ChangeEvent, useState } from "react"
import { Order } from "lib/types/generated/graphql-types"
import DELETE_ORDER from "../../graphql/mutations/order/DeleteOrder"
import { Tooltip } from "react-tooltip"
import dayjs from "dayjs"
import Popup from "../Ui/Popup"
import Input from "../Ui/Form/Input"
import { useMutation } from "@apollo/client"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { hideEditSidebar } from "lib/store/services/editSidebar/slice"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { SecureDelete } from "../Customer/DeleteCustomerOrders.styled"
import { ReactComponent as LockIcon } from "images/icons/lock.svg"
import { ReactComponent as UnlockIcon } from "images/icons/unlock.svg"
import { ReactComponent as TrashIcon } from "images/icons/trash-can.svg"
import { PermissionGroups } from "../../routes/constants"
import { WithAuth } from "../Auth"
import { InputWrapper } from "./DeleteOrder.styled"

type Props = {
  orderDetails: Order
}

const DeleteOrder = ({ orderDetails }: Props) => {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState<string>("")

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const isAfter30Days = (date: string): boolean => {
    if (!date) return false

    const orderDate = dayjs(orderDetails.date)
    const daysAgo = dayjs().subtract(30, "days")

    return orderDate.isBefore(daysAgo)
  }

  const closePopup = () => {
    setOpen(false)
  }

  const [deleteOrder, { loading }] = useMutation(DELETE_ORDER, {
    onCompleted: (data) => {
      if (data) {
        dispatch(
          alertActions.actions.createAlert({
            message: `Order ${orderDetails.reference}" successfully deleted`,
            type: "success"
          })
        )
        setOpen(false)
        dispatch(hideEditSidebar())
        navigate("/orders")
      } else {
        alertActions.actions.createAlert({
          type: "error"
        })
      }
    },
    onError: (error) => {
      setOpen(false)
      dispatch(
        alertActions.actions.createAlert({
          message: { error }.error.message,
          type: "error"
        })
      )
    }
  })

  const onSecureDelete = () => {
    deleteOrder({
      variables: {
        orderId: orderDetails.id
      }
    })
  }

  const matchInput = () => {
    return input === orderDetails.reference
  }

  if (!orderDetails) return null

  return (
    <WithAuth requiredGroups={PermissionGroups.orders.delete}>
      {!isAfter30Days(orderDetails?.date) && (
        <Tooltip id="tooltip">
          <div>Cannot delete orders created within the last 30 days</div>
        </Tooltip>
      )}
      <li onClick={(e) => {
        e.stopPropagation()
        setOpen(true)
      }}>
        <TrashIcon />
        Delete order
      </li>

      {open && (
        <Popup
          title={`Delete order ${orderDetails?.reference}`}
          subtitle={`Are you sure you want to delete the following order?`}
          item={{ itemId: orderDetails.id, itemName: "order" }}
          handleCloseClick={closePopup}
          handleOkClick={onSecureDelete}
          disableOkButton={!matchInput()}
          type="delete"
          loading={loading}
        >
          <>
            <SecureDelete>
              <p>
                Input &quot;<b>{orderDetails.reference}</b>&quot; to confirm delete:
              </p>
              <InputWrapper>
                {matchInput() ? <UnlockIcon /> : <LockIcon />}
                <Input
                  name="deleteName"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setInput(e.target.value)}
                />
              </InputWrapper>
            </SecureDelete>
          </>
        </Popup>
      )}
    </WithAuth>
  )
}

export default DeleteOrder
